import type {
    ButtonHTMLAttributes,
    ForwardedRef,
    HTMLAttributes,
    ReactNode,
} from "react";
import { forwardRef } from "react";
import treezLogo from "../../../assets/images/black-logo.svg";
import { cx } from "../../utils/cx";
import styles from "./AppHeader.module.scss";

export type AppHeaderProps = Omit<HTMLAttributes<HTMLElement>, "as"> & {
    /** Host element. Default `header`. */
    as?: "header" | "div";
    /**
     * `chrome` — **Grid** shell: direct children should be
     * `AppHeader.BrandSlot`, `AppHeader.ProfileSlot`, `AppHeader.StoreToolbar` (in that order).
     * Optional **`AppHeader.NavToggle`** before `Brand` inside `BrandSlot` toggles the aside rail;
     * hover expansion stays in `AsideMenu`. Mobile: row 1 = brand + profile; row 2 = store toolbar full width.
     * Desktop: brand | store toolbar | profile (far right).
     */
    layout?: "default" | "chrome";
};

function AppHeaderRoot({
    as: Tag = "header",
    className,
    children,
    layout = "default",
    ...rest
}: AppHeaderProps) {
    return (
        <Tag
            className={cx(styles.root, className)}
            data-layout={layout === "chrome" ? "chrome" : undefined}
            data-ui="app-header"
            {...rest}
        >
            {children}
        </Tag>
    );
}

AppHeaderRoot.displayName = "AppHeader";

export function AppHeaderBrand({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    const content = children ?? (
        <img alt="TREEZ" className={styles.brandLogo} src={treezLogo} />
    );

    return (
        <div className={cx(styles.brand, className)} {...rest}>
            {content}
        </div>
    );
}

AppHeaderBrand.displayName = "AppHeader.Brand";

/**
 * Wrap **`AppHeader.Brand`** for **`layout="chrome"`** (grid area — left column).
 */
export function AppHeaderBrandSlot({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.chromeBrandSlot, className)} {...rest}>
            {children}
        </div>
    );
}

AppHeaderBrandSlot.displayName = "AppHeader.BrandSlot";

/**
 * Wrap profile / account control for **`layout="chrome"`** (grid area — far right on desktop).
 */
export function AppHeaderProfileSlot({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.chromeProfileSlot, className)} {...rest}>
            {children}
        </div>
    );
}

AppHeaderProfileSlot.displayName = "AppHeader.ProfileSlot";

/**
 * Store row for **`layout="chrome"`** (selector + divider + status). On mobile this is
 * the second grid row, full width; on desktop the center column grows (`minmax(0,1fr)`).
 */
export function AppHeaderStoreToolbar({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.chromeStoreToolbar, className)} {...rest}>
            {children}
        </div>
    );
}

AppHeaderStoreToolbar.displayName = "AppHeader.StoreToolbar";

export function AppHeaderActions({
    className,
    children,
    ...rest
}: HTMLAttributes<HTMLDivElement>) {
    return (
        <div className={cx(styles.actions, className)} {...rest}>
            {children}
        </div>
    );
}

AppHeaderActions.displayName = "AppHeader.Actions";

export function AppHeaderDivider({
    className,
    "aria-hidden": ariaHidden = true,
    ...rest
}: HTMLAttributes<HTMLSpanElement>) {
    return (
        <span
            aria-hidden={ariaHidden}
            className={cx(styles.divider, className)}
            role="separator"
            {...rest}
        />
    );
}

AppHeaderDivider.displayName = "AppHeader.Divider";

export type AppHeaderMenuTriggerTone = "default" | "danger" | "warning";

export type AppHeaderMenuTriggerProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
> & {
    /** Small grey line above `label` (e.g. “Current store”). Omit for a single-line trigger (e.g. “Register 4”). */
    metaLabel?: ReactNode;
    /** Primary value line (e.g. store name, “Error”, or the only line when `metaLabel` is omitted). */
    label: ReactNode;
    /** Optional leading visual (e.g. `Thumbnail` icon / avatar). */
    icon?: ReactNode;
    /** Emphasis for the value line (status errors, etc.). */
    tone?: AppHeaderMenuTriggerTone;
    /**
     * `pill` = capsule: gray background only on hover, focus, or open menu (store selector /
     * store status). `plain` = no capsule (e.g. profile).
     */
    surface?: "plain" | "pill";
    /**
     * When `surface` is `pill`, below 768px use a shorter pill (e.g. Store Status in mobile header).
     */
    compactOnMobile?: boolean;
};

function MenuChevron() {
    return (
        <svg fill="none" height={18} viewBox="0 0 18 18" width={18}>
            <path
                d="M5 7l4 4 4-4"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
            />
        </svg>
    );
}

export const AppHeaderMenuTrigger = forwardRef<
    HTMLButtonElement,
    AppHeaderMenuTriggerProps
>(function AppHeaderMenuTrigger(
    {
        metaLabel,
        label,
        icon,
        tone = "default",
        surface = "plain",
        compactOnMobile = false,
        className,
        type = "button",
        ...rest
    },
    ref: ForwardedRef<HTMLButtonElement>,
) {
    const hasMeta =
        metaLabel != null &&
        (typeof metaLabel !== "string" || metaLabel.trim() !== "");

    return (
        <button
            className={cx(styles.menuTrigger, className)}
            data-pill-mobile-compact={
                surface === "pill" && compactOnMobile ? true : undefined
            }
            data-surface={surface === "pill" ? "pill" : undefined}
            data-tone={tone}
            ref={ref}
            type={type}
            {...rest}
        >
            {icon ? (
                <span className={styles.menuTriggerIcon}>{icon}</span>
            ) : null}
            <span
                className={styles.menuTriggerBody}
                data-single={hasMeta ? undefined : "true"}
            >
                {hasMeta ? (
                    <span className={styles.menuTriggerMeta}>{metaLabel}</span>
                ) : null}
                <span className={styles.menuTriggerLabel}>{label}</span>
            </span>
            <span aria-hidden className={styles.menuTriggerChevron}>
                <MenuChevron />
            </span>
        </button>
    );
});

AppHeaderMenuTrigger.displayName = "AppHeader.MenuTrigger";

/** Collapsed (default): short top bar, left-aligned, rounded caps. */
function NavMenuIconShort() {
    return (
        <svg fill="none" height={24} viewBox="0 0 24 24" width={24}>
            <path
                d="M5 7h7M5 12h14M5 17h14"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
            />
        </svg>
    );
}

/** Collapsed + `:hover`, or expanded: three equal bars. */
function NavMenuIconEqual() {
    return (
        <svg fill="none" height={24} viewBox="0 0 24 24" width={24}>
            <path
                d="M5 7h14M5 12h14M5 17h14"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2"
            />
        </svg>
    );
}

export type AppHeaderNavToggleProps = Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
> & {
    /**
     * Align with legacy aside: pass **`minimized`** (`asideMinimize || asideHover`).
     * Collapsed: **short** bar (black, no surface); **hover** → equal bars (black, no surface).
     * Expanded (`pressed === false`): equal bars, same stroke — still no surface.
     */
    pressed?: boolean;
};

/**
 * Icon-only control to **pin** the sidebar minimized/expanded (same intent as
 * `k-aside__brand-aside-toggler` in `Brand.jsx`). Wire `onClick` to your UI store /
 * `MUTATION_UI` — **does not** replace hover behavior on the aside.
 */
export const AppHeaderNavToggle = forwardRef<
    HTMLButtonElement,
    AppHeaderNavToggleProps
>(function AppHeaderNavToggle(
    {
        className,
        type = "button",
        pressed,
        "aria-label": ariaLabel = "Toggle navigation",
        ...rest
    },
    ref: ForwardedRef<HTMLButtonElement>,
) {
    const navExpanded = pressed === false;

    return (
        <button
            aria-label={ariaLabel}
            aria-pressed={pressed === undefined ? undefined : pressed}
            className={cx(styles.navToggle, className)}
            data-nav-expanded={navExpanded ? "true" : undefined}
            data-pressed={
                pressed === true
                    ? "true"
                    : pressed === false
                    ? "false"
                    : undefined
            }
            data-ui="app-header-nav-toggle"
            ref={ref}
            type={type}
            {...rest}
        >
            <span aria-hidden className={styles.navToggleIcon}>
                <span
                    className={styles.navToggleIconLayer}
                    data-nav-icon="short"
                >
                    <NavMenuIconShort />
                </span>
                <span
                    className={styles.navToggleIconLayer}
                    data-nav-icon="equal"
                >
                    <NavMenuIconEqual />
                </span>
            </span>
        </button>
    );
});

AppHeaderNavToggle.displayName = "AppHeader.NavToggle";

export const AppHeader = Object.assign(AppHeaderRoot, {
    Brand: AppHeaderBrand,
    BrandSlot: AppHeaderBrandSlot,
    ProfileSlot: AppHeaderProfileSlot,
    StoreToolbar: AppHeaderStoreToolbar,
    Actions: AppHeaderActions,
    Divider: AppHeaderDivider,
    MenuTrigger: AppHeaderMenuTrigger,
    NavToggle: AppHeaderNavToggle,
});
